/**
 * PermissionControl — LSP8 template collection + publishTemplate (payable mint fee).
 *
 * **Canonical ABI for this app:** keep the deployed contract’s ABI here (or re-sync from Hardhat
 * `artifacts/contracts/PermissionControl.sol/PermissionControl.json` → copy `abi` into this array).
 * After edits, run `npm run export:abi` (also runs before `npm run build`) to refresh `public/permission-control-abi.json` for agent `curl` access.
 * Address defaults in `constants.ts` (`PERMISSION_CONTROL.address`); override with `VITE_PERMISSIONCONTROL_CONTRACT`.
 *
 * **LSP4 on the collection:** `publishTemplate` should write `LSP4Metadata` via `_setDataForTokenId` using **LSP2 VerifiableURI**
 * bytes (`encodeLsp4MetadataVerifiableUriValue` in `utils.ts`), not raw UTF-8 string bytes — so explorers resolve metadata.
 *
 * Use `LSP4_METADATA_DATA_KEY` from `constants.ts` with `getDataForTokenId` to inspect raw values on-chain.
 */
export const PERMISSION_CONTROL_ABI = [
  {
    inputs: [],
    name: 'mintPrice',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalSupply',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'tokenOwner', type: 'address' }],
    name: 'tokenIdsOf',
    outputs: [{ internalType: 'bytes32[]', name: '', type: 'bytes32[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { internalType: 'uint32', name: 'version', type: 'uint32' },
      { internalType: 'uint256', name: 'chainId', type: 'uint256' },
      { internalType: 'uint256', name: 'permissions', type: 'uint256' },
      { internalType: 'bytes', name: 'allowedCalls', type: 'bytes' },
      { internalType: 'bytes', name: 'allowedERC725YDataKeys', type: 'bytes' },
      { internalType: 'string', name: 'metadataURI', type: 'string' },
    ],
    name: 'publishTemplate',
    outputs: [{ internalType: 'bytes32', name: 'tokenId', type: 'bytes32' }],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'publisherUP', type: 'address' },
      { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { internalType: 'uint32', name: 'version', type: 'uint32' },
    ],
    name: 'getTemplate',
    outputs: [
      {
        components: [
          { internalType: 'address', name: 'publisherUP', type: 'address' },
          { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
          { internalType: 'uint32', name: 'version', type: 'uint32' },
          { internalType: 'uint256', name: 'chainId', type: 'uint256' },
          { internalType: 'uint256', name: 'permissions', type: 'uint256' },
          { internalType: 'bytes', name: 'allowedCalls', type: 'bytes' },
          { internalType: 'bytes', name: 'allowedERC725YDataKeys', type: 'bytes' },
          { internalType: 'string', name: 'metadataURI', type: 'string' },
          { internalType: 'bool', name: 'deprecated', type: 'bool' },
        ],
        internalType: 'struct PermissionControl.TemplateData',
        name: 'template',
        type: 'tuple',
      },
      { internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'bytes32', name: 'dataKey', type: 'bytes32' }],
    name: 'getData',
    outputs: [{ internalType: 'bytes', name: 'dataValue', type: 'bytes' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'bytes32[]', name: 'dataKeys', type: 'bytes32[]' }],
    name: 'getDataBatch',
    outputs: [{ internalType: 'bytes[]', name: 'dataValues', type: 'bytes[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  /** LSP8 — read ERC725Y data scoped to a token (e.g. `LSP4Metadata` for that template NFT). */
  {
    inputs: [
      { internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
      { internalType: 'bytes32', name: 'dataKey', type: 'bytes32' },
    ],
    name: 'getDataForTokenId',
    outputs: [{ internalType: 'bytes', name: 'dataValue', type: 'bytes' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'bytes32[]', name: 'tokenIds', type: 'bytes32[]' },
      { internalType: 'bytes32[]', name: 'dataKeys', type: 'bytes32[]' },
    ],
    name: 'getDataBatchForTokenIds',
    outputs: [{ internalType: 'bytes[]', name: 'dataValues', type: 'bytes[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
      { internalType: 'bytes32', name: 'dataKey', type: 'bytes32' },
      { internalType: 'bytes', name: 'dataValue', type: 'bytes' },
    ],
    name: 'setDataForTokenId',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'publisherUP', type: 'address' },
      { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { internalType: 'uint32', name: 'version', type: 'uint32' },
    ],
    name: 'existsTemplate',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'publisherUP', type: 'address' },
      { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
    ],
    name: 'getLatestVersion',
    outputs: [{ internalType: 'uint32', name: '', type: 'uint32' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'publisherUP', type: 'address' },
      { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { internalType: 'uint32', name: 'version', type: 'uint32' },
    ],
    name: 'getTemplateTokenId',
    outputs: [{ internalType: 'bytes32', name: 'tokenId', type: 'bytes32' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'bytes32', name: 'tokenId', type: 'bytes32' }],
    name: 'getTemplateByTokenId',
    outputs: [
      {
        components: [
          { internalType: 'address', name: 'publisherUP', type: 'address' },
          { internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
          { internalType: 'uint32', name: 'version', type: 'uint32' },
          { internalType: 'uint256', name: 'chainId', type: 'uint256' },
          { internalType: 'uint256', name: 'permissions', type: 'uint256' },
          { internalType: 'bytes', name: 'allowedCalls', type: 'bytes' },
          { internalType: 'bytes', name: 'allowedERC725YDataKeys', type: 'bytes' },
          { internalType: 'string', name: 'metadataURI', type: 'string' },
          { internalType: 'bool', name: 'deprecated', type: 'bool' },
        ],
        internalType: 'struct PermissionControl.TemplateData',
        name: '',
        type: 'tuple',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
      { internalType: 'bool', name: 'deprecated', type: 'bool' },
    ],
    name: 'deprecateTemplate',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newPrice', type: 'uint256' }],
    name: 'setMintPrice',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  { stateMutability: 'payable', type: 'fallback' },
  { stateMutability: 'payable', type: 'receive' },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: 'uint256', name: 'oldPrice', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'newPrice', type: 'uint256' },
    ],
    name: 'MintPriceUpdated',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'publisherUP', type: 'address' },
      { indexed: true, internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { indexed: true, internalType: 'uint32', name: 'version', type: 'uint32' },
      { indexed: false, internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
      { indexed: false, internalType: 'uint256', name: 'chainId', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'permissions', type: 'uint256' },
      { indexed: false, internalType: 'bytes', name: 'allowedCalls', type: 'bytes' },
      { indexed: false, internalType: 'bytes', name: 'allowedERC725YDataKeys', type: 'bytes' },
      { indexed: false, internalType: 'string', name: 'metadataURI', type: 'string' },
    ],
    name: 'TemplateMinted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'publisherUP', type: 'address' },
      { indexed: true, internalType: 'bytes32', name: 'templateId', type: 'bytes32' },
      { indexed: true, internalType: 'uint32', name: 'version', type: 'uint32' },
      { indexed: false, internalType: 'bytes32', name: 'tokenId', type: 'bytes32' },
      { indexed: false, internalType: 'bool', name: 'deprecated', type: 'bool' },
    ],
    name: 'TemplateDeprecated',
    type: 'event',
  },
] as const
